import { TFunction } from 'i18next';

import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { getCommitmentTitle } from '../../utils/commitments';

export interface CommitmentCardData {
  heading: string;
  frequencyAbbr: string;
  commitments: GraphQLCommitment[];
}

export type GroupedCommitments = Record<string, GraphQLCommitment[]>;

export const groupCommitmentsByPaymentFrequency = (
  commitments: GraphQLCommitment[],
) => {
  const initialValue: GroupedCommitments = {
    // financialPledges: [], // TODO: how do we determine these?
    // [PaymentFrequencyType.OneTime]: [],
    [PaymentFrequencyType.Monthly]: [],
    [PaymentFrequencyType.Quarterly]: [],
    [PaymentFrequencyType.Semiannually]: [],
    [PaymentFrequencyType.Annually]: [],
  };

  return commitments.reduce((acc, commitment) => {
    if (commitment.paymentFrequency && commitment.paymentFrequency in acc) {
      acc[commitment.paymentFrequency] = [
        ...acc[commitment.paymentFrequency],
        commitment,
      ];
    }

    return acc;
  }, initialValue);
};

export const groupCommitmentsByType = (commitments: GraphQLCommitment[]) => {
  const initialValue: GroupedCommitments = {
    [CommitmentTypeEnum.ChildSponsorship]: [],
    [CommitmentTypeEnum.Correspondence]: [],
    [CommitmentTypeEnum.FundCommitment]: [],
    [CommitmentTypeEnum.GiftCommitment]: [],
    [CommitmentTypeEnum.Intervention]: [],
    [CommitmentTypeEnum.LivingSponsorship]: [],
  };
  return commitments.reduce((acc, commitment) => {
    if (commitment.commitmentType && commitment.commitmentType in acc) {
      acc[commitment.commitmentType] = [
        ...acc[commitment.commitmentType],
        commitment,
      ];
    }
    return acc;
  }, initialValue);
};

export const getCommitmentTotal = (commitments: GraphQLCommitment[]) =>
  commitments.reduce(
    (acc, commitment) => (commitment?.rate ? acc + commitment.rate : acc),
    0,
  );

export const groupHasCommitments = (commitmentGroups: GroupedCommitments) =>
  Object.keys(commitmentGroups).some(
    (groupName) => commitmentGroups[groupName].length > 0,
  );

export const getSortCommitmentsByTitle =
  (t: TFunction<'my', undefined>) =>
  (a: GraphQLCommitment, b: GraphQLCommitment) =>
    getCommitmentTitle(a, t)?.localeCompare(getCommitmentTitle(b, t));

/**
  Sort commitments sponsorships, gifts, and then everything else
 */
export const sortBySponsorGiftPriority = (commitments: GraphQLCommitment[]) => {
  const {
    [CommitmentTypeEnum.ChildSponsorship]: childSponsorships,
    [CommitmentTypeEnum.GiftCommitment]: giftCommitments,
    ...remainingCommitmentTypes
  } = groupCommitmentsByType(commitments);

  const remainingCommitments = Object.keys(remainingCommitmentTypes)
    .map((commitmentType) => remainingCommitmentTypes[commitmentType])
    .flat();

  return [...childSponsorships, ...giftCommitments, ...remainingCommitments];
};
